import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Space,
  message,
  Form,
  Statistic,
} from 'antd';
import { SearchOutlined, UserOutlined, UserAddOutlined, UserDeleteOutlined } from '@ant-design/icons';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import ChargingHttpHandler from '../../shared/chargingHttpHandler';
import secureStorage from '../../shared/secureStorage';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import isBetween from 'dayjs/plugin/isBetween';

// 設置 dayjs
dayjs.locale('zh-tw');
dayjs.extend(isBetween);

const { RangePicker } = DatePicker;

const UserStatusStats = () => {
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  
  // 創建固定的初始日期範圍
  const defaultDateRange = useMemo(
    () => [dayjs().subtract(30, 'days'), dayjs()],
    []
  );

  const [filters, setFilters] = useState({
    dateRange: defaultDateRange,
  });
  const [form] = Form.useForm();

  // 獲取會員資料
  const fetchMemberInfo = async () => {
    try {
      setLoading(true);
      const httpHandler = new ChargingHttpHandler(false);
      const chargingAccessToken = secureStorage.getItem('chargingAccessToken');

      const response = await httpHandler
        .axios()
        .get('/platform/get_member_info', {
          headers: { Authorization: `Bearer ${chargingAccessToken}` },
        });

      if (response.data && Array.isArray(response.data.data)) {
        await add_search_log(1);
        setMembers(response.data.data);
        message.success('會員資料載入成功');
      } else {
        await add_search_log(0);
        message.error('會員資料載入失敗');
      }
    } catch (error) {
      await add_search_log(0);
      console.error('獲取會員資料失敗', error);
      message.error('獲取會員資料失敗');
    } finally {
      setLoading(false);
    }
  };

  const add_search_log = async (success) => {
    const userRole = secureStorage.getItem('userRole');
    const httpHandler = new ChargingHttpHandler(false);
    const chargingAccessToken = secureStorage.getItem('chargingAccessToken');
    
    try {
      await httpHandler
        .axios()
        .post(
          '/platform/update_user_log_table',
          {
            log_time: dayjs().format('YYYY/MM/DD HH:mm:ss'),
            user_role: userRole,
            action_type: '用戶狀態統計搜尋',
            action_content: '用戶搜尋用戶開通狀態統計資訊',
            status: success,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${chargingAccessToken}`,
            },
          }
        );
    } catch (e) {
      console.error('寫入搜尋紀錄失敗', e);
    }
  };

  // 處理搜尋
  const handleSearch = () => {
    form.submit();
  };

  // 處理表單提交
  const handleFormSubmit = (values) => {
    setFilters({
      dateRange: values.dateRange,
    });
    message.success('搜尋完成');
  };

  // 計算統計數據
  const calculateStats = () => {
    // 有效人數（is_deleted 為 0）
    const activeUsers = members.filter(member => member.is_deleted === 0).length;

    const { dateRange } = filters;
    if (!dateRange || dateRange.length !== 2) {
      return {
        activeUsers,
        todayOpened: 0,
        todayClosed: 0,
      };
    }

    const [startDate, endDate] = dateRange;

    // 當天開通的人數（opened_at 有值且在選擇的日期範圍內）
    const todayOpened = members.filter(member => {
      if (!member.opened_at) return false;
      const openedDate = dayjs(member.opened_at);
      return openedDate.isBetween(startDate, endDate, 'day', '[]');
    }).length;

    // 當天取消開通的人數（closed_at 有值且在選擇的日期範圍內）
    const todayClosed = members.filter(member => {
      if (!member.closed_at) return false;
      const closedDate = dayjs(member.closed_at);
      return closedDate.isBetween(startDate, endDate, 'day', '[]');
    }).length;

    return {
      activeUsers,
      todayOpened,
      todayClosed,
    };
  };

  // 生成圖表數據（統計開通部分）
  const generateChartData = () => {
    if (!members.length) return [];

    const { dateRange } = filters;
    if (!dateRange || dateRange.length !== 2) return [];

    const [startDate, endDate] = dateRange;

    // 按日期分組統計
    const dateGroups = {};
    let currentDate = startDate.clone();
    while (currentDate.isSameOrBefore(endDate, 'day')) {
      const dateStr = currentDate.format('MM/DD');
      dateGroups[dateStr] = {
        date: dateStr,
        開通人數: 0,
      };
      currentDate = currentDate.clone().add(1, 'day');
    }

    // 統計每天的開通人數
    members.forEach((member) => {
      if (!member.opened_at) return;
      
      const openedDate = dayjs(member.opened_at);
      if (openedDate.isBetween(startDate, endDate, 'day', '[]')) {
        const dateStr = openedDate.format('MM/DD');
        if (dateGroups[dateStr]) {
          dateGroups[dateStr]['開通人數'] += 1;
        }
      }
    });

    return Object.values(dateGroups);
  };

  // 重置篩選
  const resetFilters = () => {
    form.setFieldsValue({
      dateRange: defaultDateRange,
    });
    setFilters({
      dateRange: defaultDateRange,
    });
  };

  useEffect(() => {
    fetchMemberInfo();
  }, []);

  const stats = calculateStats();
  const chartData = generateChartData();

  return (
    <div>
      {/* 篩選區域 */}
      <Card style={{ marginBottom: 16 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          initialValues={{
            dateRange: defaultDateRange,
          }}>
          <Row gutter={[16, 16]} align="middle">
            <Col span={12}>
              <Form.Item label="日期範圍" name="dateRange">
                <RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12} style={{ textAlign: 'right', paddingTop: 30 }}>
              <Space>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  loading={loading}>
                  搜尋
                </Button>
                <Button onClick={resetFilters}>重置篩選</Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* 統計卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={
                <span>
                  <UserOutlined style={{ marginRight: 8 }} />
                  有效用戶
                </span>
              }
              value={stats.activeUsers}
              suffix="人"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={
                <span>
                  <UserAddOutlined style={{ marginRight: 8 }} />
                  選擇範圍內開通用戶
                </span>
              }
              value={stats.todayOpened}
              suffix="人"
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={
                <span>
                  <UserDeleteOutlined style={{ marginRight: 8 }} />
                  選擇範圍內取消開通用戶
                </span>
              }
              value={stats.todayClosed}
              suffix="人"
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 圖表區域 */}
      <Card>
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>用戶開通統計報表</h3>
        </div>

        {/* 長條圖 */}
        <div style={{ height: 500, width: '100%' }}>
          {chartData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '100px 0', color: '#999' }}>
              請選擇日期範圍後點擊搜尋以查看統計資料
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="開通人數" fill="#1890ff" name="開通人數" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>
    </div>
  );
};

export default UserStatusStats;
